import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  QrCode, 
  FileText, 
  User, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  Shield,
  Users,
  Target,
  Calendar,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import ClubSwitcher from './ClubSwitcher';
import { playTeamSound } from '../utils/teamSound';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [hideBottomNav, setHideBottomNav] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isSuperAdmin, isAdmin, isOrgAdmin, isRangeOfficer, impersonationContext, stopImpersonation } = useAuth();
  const { t, initializeLanguage } = useLanguage();

  // Hide bottom nav on downward scroll, reveal on upward scroll (mobile UX)
  useEffect(() => {
    let lastY = typeof window !== 'undefined' ? window.scrollY : 0;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const diff = y - lastY;
        if (y > 120 && diff > 6) {
          setHideBottomNav(true);
        } else if (diff < -6 || y < 60) {
          setHideBottomNav(false);
        }
        lastY = y;
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Fetch unread message count
  const fetchUnreadCount = async () => {
    // Skip if messages module is disabled
    if (user?.organization?.messages_enabled === false) {
      setUnreadMessageCount(0);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API_BASE}/api/messages/unread`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadMessageCount(response.data.unread_count || 0);
      playTeamSound(response.data.messages || []);
    } catch (error) {
      // Silently fail - not critical
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    // Refresh every 15 seconds for faster notification updates
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
  }, [user, user?.organization?.messages_enabled]);

  // Listen for messageRead events to update count immediately
  useEffect(() => {
    const handleMessageRead = () => {
      // Small delay to ensure backend has processed the read status
      setTimeout(fetchUnreadCount, 100);
    };

    window.addEventListener('messageRead', handleMessageRead);
    return () => window.removeEventListener('messageRead', handleMessageRead);
  }, [user?.organization?.messages_enabled]);

  // Initialize language when user becomes available
  useEffect(() => {
    if (user?.organization_id) {
      initializeLanguage(user.organization_id, user.role);
    }
  }, [user?.organization_id, user?.role, initializeLanguage]);

  // Debug logging for navigation
  console.log('Navigation render - User:', user);
  console.log('Navigation render - isSuperAdmin:', isSuperAdmin);
  console.log('Navigation render - isOrgAdmin:', isOrgAdmin);
  console.log('Navigation render - impersonationContext:', impersonationContext);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleStopImpersonation = () => {
    if (stopImpersonation()) {
      navigate('/super-admin');
    }
  };

  const navItems = [
    { path: '/', icon: Home, label: t('nav.home', 'Hjem'), roles: ['all'] },
    { path: '/scanner', icon: QrCode, label: t('nav.scanner', 'Skann'), roles: ['all'] },
    { path: '/log', icon: FileText, label: t('nav.log', 'Min logg'), roles: ['all'] },
    { path: '/calendar', icon: Calendar, label: t('nav.calendar', 'Kalender'), roles: ['all'], requiresCalendar: true },
    { path: '/profile', icon: User, label: t('nav.profile', 'Profil'), roles: ['all'] },
    { path: '/admin', icon: Settings, label: t('nav.admin', 'SKYTELEDER'), roles: ['admin', 'org_admin', 'range_officer'] },
    { path: '/club-operations', icon: Shield, label: t('nav.club_operations', 'KLUBB DRIFT'), roles: ['admin', 'org_admin', 'range_officer'], requiresClubOperations: true },
    { path: '/super-admin', icon: Shield, label: t('nav.super_admin', 'Super Admin'), roles: ['super_admin'] }
  ];

  const canAccessRoute = (routeRoles, requiresCalendar = false, requiresClubOperations = false) => {
    // Check if calendar is required but not enabled for the organization
    if (requiresCalendar && user?.organization?.calendar_enabled === false) {
      return false;
    }
    // Check if club operations is required but not enabled for the organization
    if (requiresClubOperations && user?.organization?.club_operations_enabled !== true) {
      return false;
    }
    if (routeRoles.includes('all')) return true;
    if (isSuperAdmin && routeRoles.includes('super_admin')) return true;
    if (isAdmin && routeRoles.includes('admin')) return true;
    if (isOrgAdmin && routeRoles.includes('org_admin')) return true;
    if (isRangeOfficer && routeRoles.includes('range_officer')) return true;
    return false;
  };

  return (
    <>
      <nav className="bg-gray-800 border-b border-yellow-500/20">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo & Brand (dropdown ClubSwitcher for multi-club, static logo for single-club) */}
            <div className="flex items-center">
              <ClubSwitcher logo />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden xl:flex items-center space-x-2 2xl:space-x-6 min-w-0">
              {navItems.map((item) => {
                if (!canAccessRoute(item.roles, item.requiresCalendar, item.requiresClubOperations)) return null;
                
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const showBadge = item.path === '/' && unreadMessageCount > 0;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`relative flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-yellow-500 text-gray-900'
                        : 'text-gray-300 hover:text-white hover:bg-gray-700'
                    }`}
                  >
                    <div className="relative">
                      <Icon className="h-4 w-4" />
                      {/* Badge for unread messages on Hjem */}
                      {showBadge && (
                        <span className="absolute -top-2 -right-2 min-w-[16px] h-[16px] flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full px-1">
                          {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                        </span>
                      )}
                    </div>
                    <span>{item.label}</span>
                  </Link>
                );
              })}
              
              {/* User Info and Logout */}
              <div className="flex items-center space-x-4">
                <ClubSwitcher />
                <div className="text-sm text-gray-300">
                  <div className="font-medium">{user?.full_name}</div>
                  <div className="text-xs text-yellow-500 capitalize">
                    {impersonationContext ? 'Super Admin (Impersonating)' : user?.role?.replace('_', ' ')}
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-1 text-gray-300 hover:text-white transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="sr-only">{t('nav.logout', 'Logg ut')}</span>
                </button>
              </div>
            </div>

            {/* Mobile menu button + compact club switcher */}
            <div className="xl:hidden flex items-center gap-2">
              <ClubSwitcher compact />
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-gray-300 hover:text-white p-2"
              >
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Top Navigation (simplified) */}
        {isOpen && (
          <div className="xl:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-gray-800">
              {/* Only user info and logout in top dropdown */}
              <div className="px-3 py-2">
                <div className="text-base font-medium text-white">{user?.full_name}</div>
                <div className="text-sm text-gray-300 capitalize">
                  {impersonationContext ? 'Super Admin (Impersonating)' : user?.role?.replace('_', ' ')}
                </div>
                <div className="mt-2">
                  <ClubSwitcher />
                </div>
              </div>
              
              {/* Club Operations link - only for admins/skyteledere when enabled */}
              {(isAdmin || isRangeOfficer || isOrgAdmin) && user?.organization?.club_operations_enabled === true && (
                <Link
                  to="/club-operations"
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center space-x-2 px-3 py-2 text-base font-medium rounded-md w-full ${
                    location.pathname === '/club-operations'
                      ? 'bg-yellow-500 text-gray-900'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  <Shield className="h-5 w-5" />
                  <span>Klubb drift</span>
                </Link>
              )}
              
              {impersonationContext && (
                <button
                  onClick={handleStopImpersonation}
                  className="flex items-center space-x-2 px-3 py-2 text-base font-medium text-yellow-500 hover:text-yellow-400 hover:bg-gray-700 w-full"
                >
                  <Shield className="h-5 w-5" />
                  <span>Stopp Impersonation</span>
                </button>
              )}
              
              {/* Reload Aktivlogg - Force refresh to get latest version */}
              <button
                onClick={() => {
                  setIsOpen(false);
                  // Clear all caches and force reload
                  if ('caches' in window) {
                    caches.keys().then(names => {
                      names.forEach(name => caches.delete(name));
                    });
                  }
                  // Force hard reload bypassing cache (like Shift+Reload)
                  window.location.reload(true);
                }}
                className="flex items-center space-x-2 px-3 py-2 text-base font-medium text-blue-400 hover:text-blue-300 hover:bg-gray-700 w-full"
              >
                <RefreshCw className="h-5 w-5" />
                <span>Reload Aktivlogg</span>
              </button>
              
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-3 py-2 text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700 w-full"
              >
                <LogOut className="h-5 w-5" />
                <span>{t('nav.logout', 'Logg ut')}</span>
              </button>
            </div>
          </div>
        )}

        {/* Mobile Bottom Navigation Bar - Large touch targets, high z-index, active state feedback, hides on scroll down */}
        <div className={`fixed bottom-0 left-0 right-0 bg-yellow-500 border-t border-yellow-600 xl:hidden z-[100] shadow-[0_-2px_10px_rgba(0,0,0,0.15)] transform transition-transform duration-300 ${hideBottomNav ? 'translate-y-full' : 'translate-y-0'}`}>
          <div className="flex justify-around items-stretch px-1 safe-area-inset-bottom">
            {/* Main navigation items for mobile - subtle haptic tick on tap (supported browsers only; iOS Safari ignores gracefully) */}
            {(() => {
              const hapticTap = () => {
                try {
                  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
                    navigator.vibrate(10);
                  }
                } catch (_) { /* noop */ }
              };
              const baseItems = [
                { path: '/', icon: Home, label: t('nav.home', 'Hjem'), badge: unreadMessageCount > 0 ? unreadMessageCount : null },
                { path: '/scanner', icon: QrCode, label: t('nav.scanner', 'Skann') },
                { path: '/log', icon: FileText, label: t('nav.log', 'Min logg') }
              ];
              
              // Add Calendar if enabled for the organization
              if (user?.organization?.calendar_enabled !== false) {
                baseItems.push({ path: '/calendar', icon: Calendar, label: 'Kalender' });
              }

              // Add Skyteleder button only for admins, range officers (skyteledere) and org admins
              if (isAdmin || isRangeOfficer || isOrgAdmin) {
                baseItems.push({ path: '/admin', icon: Target, label: 'Skyteleder' });
              }

              // Always add profile as last item
              baseItems.push({ path: '/profile', icon: User, label: user?.full_name?.split(' ')[0] || t('nav.profile', 'Profil') });

              return baseItems.map((item, index) => {
                // Profile item (always last)
                if (item.path === '/profile') {
                  return (
                    <Link
                      key="profile-mobile"
                      to="/profile"
                      onClick={hapticTap}
                      className="flex flex-col items-center justify-center flex-1 min-h-[52px] px-1 py-1 text-gray-900 cursor-pointer select-none active:bg-yellow-600/40 transition-colors"
                      data-testid="mobile-nav-profile"
                    >
                      <User className="h-5 w-5 mb-1 pointer-events-none" />
                      <span className="text-xs font-medium truncate max-w-16 pointer-events-none">
                        {user?.full_name?.split(' ')[0] || 'Profil'}
                      </span>
                    </Link>
                  );
                }
                
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={hapticTap}
                    data-testid={`mobile-nav-${item.path.replace('/', '') || 'home'}`}
                    className={`relative flex flex-col items-center justify-center flex-1 min-h-[52px] px-1 py-1 cursor-pointer select-none active:bg-yellow-600/40 transition-colors ${
                      isActive
                        ? 'text-gray-800'
                        : 'text-gray-700 hover:text-gray-800'
                    }`}
                  >
                    <div className="relative pointer-events-none">
                      <Icon className="h-5 w-5 mb-1" />
                      {/* Badge for unread messages */}
                      {item.badge && (
                        <span className="absolute -top-1 -right-2 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1">
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </div>
                    <span className="text-xs font-medium pointer-events-none">{item.label}</span>
                  </Link>
                );
              });
            })()}
          </div>
        </div>
      </nav>

      {/* Impersonation Banner */}
      {impersonationContext && (
        <div className="bg-orange-600 text-white px-4 py-2 border-b border-orange-700">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Users className="h-4 w-4" />
              <span className="text-sm font-medium">
                Impersonerer organisasjon: <strong>{impersonationContext.orgName}</strong>
              </span>
            </div>
            <button
              onClick={handleStopImpersonation}
              className="flex items-center space-x-1 text-xs font-medium hover:text-orange-200 transition-colors"
            >
              <Shield className="h-3 w-3" />
              <span>Tilbake til Super Admin</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;